/**
 * Fonts that motion graphics bring with them (they are not on any layer, so renderers would not
 * load them otherwise). Kept free of imports: the template schema module uses it.
 */
export const FX_MONO_FONT = 'JetBrains Mono';

/** Kinds drawn with their own font instead of the headline's. */
export const FX_KIND_FONTS: Record<string, string> = {
  hud: FX_MONO_FONT,
  terminal: FX_MONO_FONT,
  code: FX_MONO_FONT,
  counter: FX_MONO_FONT,
  bars: FX_MONO_FONT,
  brackets: FX_MONO_FONT,
  'split-flap': FX_MONO_FONT,
  donut: FX_MONO_FONT,
  ticker: FX_MONO_FONT,
};

const TEXT_PARAMS = ['text', 'title', 'label', 'label2', 'label3', 'prompt', 'prefix', 'suffix'];

/** Fonts of a document's motion graphics with the text they draw (to load the right subsets). */
export function fxFontsUsed(doc: {
  layers?: Array<{
    id: string;
    type: string;
    text?: string;
    motion?: { effect?: { kind: string; params?: Record<string, unknown> } | null };
  }>;
  motion?: { fx?: Array<{ kind: string; params?: Record<string, unknown> }> };
}): Array<{ family: string; text: string }> {
  const out = new Map<string, string>();
  // Layer text effects count as effects sourcing their layer
  const effects: Array<{ kind: string; params?: Record<string, unknown> }> = (doc.layers ?? [])
    .filter((l) => l.motion?.effect)
    .map((l) => ({
      kind: l.motion!.effect!.kind,
      params: { ...l.motion!.effect!.params, source: l.id },
    }));
  for (const fx of [...(doc.motion?.fx ?? []), ...effects]) {
    // A font picked for a text effect
    const picked = typeof fx.params?.font === 'string' ? fx.params.font.trim() : '';
    if (picked && !picked.startsWith('brand:')) {
      const source = doc.layers?.find((l) => l.id === fx.params?.source);
      const text = [...TEXT_PARAMS.map((k) => fx.params?.[k]), source?.text]
        .filter((v): v is string => typeof v === 'string')
        .join(' ');
      out.set(picked, `${out.get(picked) ?? ''} ${text} 0123456789`);
    }
    const family = FX_KIND_FONTS[fx.kind];
    if (!family) continue;
    const text = TEXT_PARAMS.map((k) => fx.params?.[k])
      .filter((v): v is string => typeof v === 'string')
      .join(' ');
    out.set(family, `${out.get(family) ?? ''} ${text} 0123456789:%+.`);
  }
  return [...out].map(([family, text]) => ({ family, text }));
}
